// Reading a bank statement into the rows the API takes, from the CSV, Excel (.xlsx, or the HTML-table ".xls" many Indian
// banks give) or PDF download. Column names vary by bank; these cover the usual ones (HDFC, ICICI, SBI, Axis, Kotak, Yes,
// IndusInd, PNB, Bank of Baroda, Canara, IDFC First): date, narration / particulars, ref / cheque no., withdrawal / debit,
// deposit / credit (or one amount with a Dr / Cr column, or a signed amount), balance. Lines without a date continue
// the narration above them.
import { parseCsv } from "../lib/csv";

export interface BankRow { date: string; value_date?: string; description: string; reference?: string; amount: number; balance?: number }
export interface Read { rows: BankRow[]; problems: string[] }

export const money = (s: string | undefined) => {
  if (s == null) return undefined;
  const t = s.replace(/[,₹\s]/g, "").replace(/^\((.*)\)$/, "-$1").replace(/^INR/i, "");
  if (t === "" || t === "-") return undefined;
  const cr = /cr\.?$/i.test(t), dr = /dr\.?$/i.test(t);
  const n = Number(t.replace(/(cr|dr)\.?$/i, ""));
  return Number.isNaN(n) ? undefined : dr ? -Math.abs(n) : cr ? Math.abs(n) : n;
};
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
export function bankDate(s: string | undefined): string | undefined {
  if (!s) return undefined;
  const t = s.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})[/.\- ](\d{1,2}|[A-Za-z]{3,9})[/.\-, ]+(\d{2}|\d{4})$/.exec(t);
  if (!m) return undefined;
  const mo = /\d/.test(m[2]) ? Number(m[2]) : MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1;
  const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  if (mo < 1 || mo > 12 || Number(m[1]) < 1 || Number(m[1]) > 31) return undefined;
  return `${y}-${String(mo).padStart(2, "0")}-${m[1].padStart(2, "0")}`;
}
// an Excel serial date (days from 1899-12-30)
export const excelDate = (n: number) => new Date(Date.UTC(1899, 11, 30) + Math.round(n) * 864e5).toISOString().slice(0, 10);

const norm = (h: string) => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const isHeader = (cells: string[]) => {
  const n = cells.map(norm);
  return n.some((c) => /date/.test(c)) && n.some((c) => /narration|description|particular|remark|detail|transaction_details/.test(c))
    && n.some((c) => /withdraw|debit|deposit|credit|amount|^dr$|^cr$/.test(c));
};

// a table (rows of cells) → bank rows
export function readBankTable(table: string[][]): Read {
  const start = table.findIndex(isHeader);
  const problems: string[] = [];
  if (start < 0) return { rows: [], problems: ["Couldn't find the table's heading (Date, Narration, Withdrawal / Deposit …)"] };
  const keys = table[start].map(norm);
  const col = (re: RegExp, not?: RegExp) => keys.findIndex((k) => re.test(k) && !(not && not.test(k)));
  const cDate = (() => { const i = col(/^(txn_|transaction_|tran_|posting_)?date$|^date$/); return i >= 0 ? i : col(/date/, /value/); })();
  const cValue = col(/value_d(a)?te?|value_dt/);
  const cDesc = col(/narration|description|particular|remark|detail/);
  const cRef = col(/ref|chq|cheque|instrument/);
  const cDr = col(/withdraw|debit|^dr$|paid_out/, /dr_cr|cr_dr/);
  const cCr = col(/deposit|credit|^cr$|paid_in/, /dr_cr|cr_dr/);
  const cAmt = col(/^amount|txn_amount|transaction_amount|^amt/);
  const cType = col(/^dr_cr$|^cr_dr$|^type$|^dr_cr_|debit_credit|^d_c$/);
  const cBal = col(/balance/);
  const rows: BankRow[] = [];
  const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "").trim() : "");
  table.slice(start + 1).forEach((r, i) => {
    if (!r.some((x) => (x ?? "").trim())) return;
    const rawDate = cell(r, cDate);
    const date = bankDate(rawDate) ?? (/^\d{5}(\.\d+)?$/.test(rawDate) ? excelDate(Number(rawDate)) : undefined);
    if (!date) {
      // a narration carried onto the next line, or the totals / closing lines at the foot
      const more = cell(r, cDesc);
      if (rows.length && more && !cell(r, cDr) && !cell(r, cCr) && !cell(r, cAmt)) rows[rows.length - 1].description += " " + more;
      else if (r.some((x) => /\d/.test(x ?? "")) && !/total|closing|opening|balance|statement|page/i.test(r.join(" "))) problems.push(`Row ${start + i + 2}: no date I can read`);
      return;
    }
    const dr = money(cell(r, cDr)), cr = money(cell(r, cCr));
    let amount = dr || cr ? (cr ?? 0) - Math.abs(dr ?? 0) : money(cell(r, cAmt));
    const type = cell(r, cType).toLowerCase();
    if (amount != null && cType >= 0 && type) amount = /^(dr|d|debit|withdrawal)/.test(type) ? -Math.abs(amount) : Math.abs(amount);
    if (!amount) { if (!/opening|b\/f|brought forward/i.test(cell(r, cDesc))) problems.push(`Row ${start + i + 2}: no amount`); return; }
    const vd = cell(r, cValue);
    rows.push({ date, value_date: bankDate(vd) ?? (/^\d{5}$/.test(vd) ? excelDate(Number(vd)) : undefined), description: cell(r, cDesc),
                reference: cell(r, cRef) || undefined, amount: Math.round(amount * 100) / 100, balance: money(cell(r, cBal)) });
  });
  return { rows, problems };
}

export const readBankCsv = (text: string): Read => readBankTable(parseCsv(text.replace(/^﻿/, "")));

// ---------- "Excel" that is really an HTML table (SBI, PNB, many others) ----------
const unescape = (s: string) => s.replace(/<br\s*\/?>/gi, " ").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
  .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n))).replace(/\s+/g, " ").trim();
export function readHtmlTable(html: string): string[][] {
  const rows: string[][] = [];
  for (const tr of html.match(/<tr[\s\S]*?<\/tr>/gi) ?? []) {
    const cells = [...tr.matchAll(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi)].map((m) => unescape(m[1]));
    if (cells.length) rows.push(cells);
  }
  return rows;
}

// ---------- .xlsx: the first sheet, read straight from the zip ----------
export async function readXlsx(buf: ArrayBuffer): Promise<string[][]> {
  const { unzipSync, strFromU8 } = await import("fflate");
  const files = unzipSync(new Uint8Array(buf));
  const text = (p: string) => (files[p] ? strFromU8(files[p]) : "");
  const shared = [...text("xl/sharedStrings.xml").matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => [...m[1].matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => unescape(t[1])).join(""));
  // date-formatted cells (by style) are turned into dates
  const styles = text("xl/styles.xml");
  const dateFmts = new Set([14, 15, 16, 17, 22, ...[...styles.matchAll(/<numFmt numFmtId="(\d+)" formatCode="([^"]*)"/g)].filter((m) => /[dy]/i.test(m[2]) && !/[#0]/.test(m[2])).map((m) => Number(m[1]))]);
  const xfs = [...(styles.match(/<cellXfs[\s\S]*?<\/cellXfs>/)?.[0] ?? "").matchAll(/<xf [^>]*numFmtId="(\d+)"/g)].map((m) => Number(m[1]));
  const rel = /<sheet [^>]*r:id="([^"]+)"/.exec(text("xl/workbook.xml"))?.[1];
  const target = rel ? new RegExp(`Id="${rel}"[^>]*Target="([^"]+)"`).exec(text("xl/_rels/workbook.xml.rels"))?.[1] : undefined;
  const sheet = text(target ? "xl/" + target.replace(/^\/?xl\//, "").replace(/^\//, "") : "xl/worksheets/sheet1.xml");
  const colNo = (ref: string) => [...ref.replace(/\d+/g, "")].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0) - 1;
  const rows: string[][] = [];
  for (const rm of sheet.matchAll(/<row[^>]*>([\s\S]*?)<\/row>/g)) {
    const r: string[] = [];
    for (const cm of rm[1].matchAll(/<c ([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = cm[1], inner = cm[2] ?? "";
      const ref = /r="([A-Z]+\d+)"/.exec(attrs)?.[1]; const t = /t="(\w+)"/.exec(attrs)?.[1]; const s = Number(/s="(\d+)"/.exec(attrs)?.[1] ?? -1);
      const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
      let val = t === "s" ? shared[Number(v)] ?? "" : t === "inlineStr" ? unescape(/<t[^>]*>([\s\S]*?)<\/t>/.exec(inner)?.[1] ?? "") : unescape(v ?? "");
      if (!t && v && s >= 0 && dateFmts.has(xfs[s] ?? -1)) val = excelDate(Number(v));
      r[ref ? colNo(ref) : r.length] = val;
    }
    rows.push(Array.from(r, (x) => x ?? ""));
  }
  return rows;
}

// ---------- PDF: text with positions → transaction rows ----------
export interface PdfLine { y: number; items: { str: string; x: number; w: number }[] }
const NUM = /^-?\(?[\d,]+\.\d{2}\)?(\s?(cr|dr)\.?)?$/i;
const DATE_AT_START = /^(\d{1,2}[/.\- ](\d{1,2}|[A-Za-z]{3,9})[/.\- ,]+(\d{2}|\d{4}))\b/;

// the lines of a statement's pages → rows: a line starting with a date opens a transaction; the amounts are the numbers
// at its end (the last is the balance); whether it is money in or out comes from the balance moving, else from which
// heading (withdrawal / deposit) the amount sits under; lines without a date add to the narration
export function pdfLinesToRows(lines: PdfLine[]): Read {
  const problems: string[] = [];
  const rows: BankRow[] = [];
  let drX: number | undefined, crX: number | undefined, prevBal: number | undefined, inTable = false;
  for (const line of lines) {
    const text = line.items.map((i) => i.str).join(" ").replace(/\s+/g, " ").trim();
    if (!text) continue;
    const words = text.toLowerCase();
    if (/date/.test(words) && /(withdraw|debit|deposit|credit)/.test(words) && /balance/.test(words)) {
      inTable = true;
      const at = (re: RegExp) => { const it = line.items.find((i) => re.test(i.str.toLowerCase())); return it ? it.x + it.w / 2 : undefined; };
      drX = at(/withdraw|debit/); crX = at(/deposit|credit/);
      continue;
    }
    const ob = /opening balance|balance b\/f|brought forward/i.test(text) ? line.items.map((i) => money(i.str)).filter((n) => n != null).pop() : undefined;
    if (ob != null) { prevBal = ob; continue; }
    const dm = DATE_AT_START.exec(text);
    const date = dm ? bankDate(dm[1]) : undefined;
    const nums = line.items.filter((i) => NUM.test(i.str.trim()));
    if (!date) {
      if (inTable && rows.length && !nums.length && !/page \d|statement|total|closing balance|generated/i.test(text)) rows[rows.length - 1].description += " " + text;
      continue;
    }
    if (!inTable) inTable = true;
    if (nums.length === 0) { problems.push(`${text.slice(0, 40)}…: no amount`); continue; }
    const balItem = nums.length >= 2 ? nums[nums.length - 1] : undefined;
    const amtItem = nums.length >= 2 ? nums[nums.length - 2] : nums[0];
    const bal = balItem ? money(balItem.str) : undefined;
    let amt = Math.abs(money(amtItem.str) ?? 0);
    if (bal != null && prevBal != null && Math.abs(Math.abs(bal - prevBal) - amt) < 0.011) amt = bal >= prevBal ? amt : -amt;
    else if (drX != null && crX != null) { const c = amtItem.x + amtItem.w / 2; amt = Math.abs(c - crX) < Math.abs(c - drX) ? amt : -amt; }
    else if (/dr\.?$/i.test(amtItem.str.trim())) amt = -amt;
    if (bal != null) prevBal = bal;
    // the narration: what lies between the date(s) and the amounts
    const firstNum = line.items.indexOf(nums[Math.max(0, nums.length - 2)]);
    let desc = line.items.slice(0, firstNum >= 0 ? firstNum : undefined).map((i) => i.str).join(" ").replace(/\s+/g, " ").trim().slice(dm![0].length).trim();
    // a value date right after the date, or at the end of the narration column
    const vd = DATE_AT_START.exec(desc) ?? /\s(\d{1,2}[/.\-]\d{1,2}[/.\-]\d{2,4})$/.exec(desc);
    const value_date = vd ? bankDate(vd[1]) : undefined;
    if (vd) desc = vd.index === 0 ? desc.slice(vd[0].length).trim() : desc.slice(0, vd.index).trim();
    const ref = /\b(?:chq|cheque|ref)[.:]?\s*(\w{4,})/i.exec(desc)?.[1];
    rows.push({ date, ...(value_date ? { value_date } : {}), description: desc, ...(ref ? { reference: ref } : {}), amount: Math.round(amt * 100) / 100, ...(bal != null ? { balance: bal } : {}) });
  }
  if (!rows.length) problems.push("Couldn't find transactions in this PDF; download the statement as Excel or CSV instead");
  return { rows, problems };
}

// the same with a pdf.js the caller has set up (the email-in worker brings its own)
export interface PdfJs { getDocument: (o: Record<string, unknown>) => { promise: Promise<{ numPages: number; getPage: (n: number) => Promise<{ getTextContent: () => Promise<{ items: unknown[] }> }> }> } }
export async function readPdfWith(pdfjs: PdfJs, buf: ArrayBuffer, password?: string, opts: Record<string, unknown> = {}): Promise<Read> {
  const doc = await pdfjs.getDocument({ data: new Uint8Array(buf.slice(0)), password, ...opts }).promise;
  const lines: PdfLine[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const tc = await page.getTextContent();
    const items = (tc.items as { str: string; transform: number[]; width: number }[]).filter((i) => i.str.trim())
      .map((i) => ({ str: i.str.trim(), x: i.transform[4], y: i.transform[5], w: i.width }));
    // one line per y (within 2.5 points), top to bottom, left to right
    const byY: { y: number; items: typeof items }[] = [];
    for (const it of items.sort((a, b) => b.y - a.y || a.x - b.x)) {
      const l = byY.find((x) => Math.abs(x.y - it.y) < 2.5);
      if (l) l.items.push(it); else byY.push({ y: it.y, items: [it] });
    }
    for (const l of byY) lines.push({ y: l.y, items: l.items.sort((a, b) => a.x - b.x).map(({ str, x, w }) => ({ str, x, w })) });
  }
  return pdfLinesToRows(lines);
}

// any statement file: by its type and content
export async function readStatementBytes(fileName: string, buf: ArrayBuffer, password: string | undefined,
                                         pdf: (b: ArrayBuffer, pw?: string) => Promise<Read>): Promise<Read> {
  const name = fileName.toLowerCase();
  const head = new TextDecoder("utf-8", { fatal: false }).decode(buf.slice(0, 512)).trimStart();
  if (name.endsWith(".pdf") || head.startsWith("%PDF")) return pdf(buf, password);
  if (/^<(!doctype|html|table|\?xml)/i.test(head) && !head.startsWith("<?xml version=\"1.0\" encoding=\"UTF-8\" standalone")) {
    return readBankTable(readHtmlTable(new TextDecoder().decode(buf)));
  }
  const bytes = new Uint8Array(buf.slice(0, 4));
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return readBankTable(await readXlsx(buf));   // a zip: .xlsx
  if (bytes[0] === 0xd0 && bytes[1] === 0xcf) return { rows: [], problems: ["This is an old-style .xls file: open it and save it as .xlsx or CSV, then import that"] };
  return readBankCsv(new TextDecoder().decode(buf));
}
